import type { ResolverMap } from './ResolverMap'
import { ApiProblemReasonSchema } from '~/services/api/generated'
import type { ApiErrorCode, ApiProblemReason } from '~/services/api/generated'

const apiErrorByCode = {
  INVALID_REQUEST: 'presentation.errors.apiError.invalidRequest',
  AUTHENTICATION_REQUIRED: 'presentation.errors.apiError.authenticationRequired',
  AUTHORIZATION_DENIED: 'presentation.errors.apiError.authorizationDenied',
  STEP_UP_REQUIRED: 'presentation.errors.apiError.stepUpRequired',
  PASSWORD_CONFIRMATION_REQUIRED: 'presentation.errors.apiError.passwordConfirmationRequired',
  ADMIN_PANEL_REDIRECT_REQUIRED: 'presentation.errors.apiError.adminPanelRedirectRequired',
  RESOURCE_NOT_FOUND: 'presentation.errors.apiError.resourceNotFound',
  METHOD_NOT_ALLOWED: 'presentation.errors.apiError.methodNotAllowed',
  CONFLICT: 'presentation.errors.apiError.conflict',
  VALIDATION_ERROR: 'presentation.errors.apiError.validationError',
  BUSINESS_RULE_VIOLATION: 'presentation.errors.apiError.businessRuleViolation',
  RATE_LIMITED: 'presentation.errors.apiError.rateLimited',
  EXTERNAL_SERVICE_ERROR: 'presentation.errors.apiError.externalServiceError',
  SERVICE_UNAVAILABLE: 'presentation.errors.apiError.serviceUnavailable',
  SESSION_EXPIRED: 'presentation.errors.apiError.sessionExpired',
  INTERNAL_ERROR: 'presentation.errors.apiError.internalError'
} satisfies Record<ApiErrorCode, string>

// Exhaustive over ApiProblemReason: every ApiErrorCode broad group each reason nests under (usually one).
const apiProblemReasonBroadCode = {
  PAYMENT_PROCESSING_FAILED: ['BUSINESS_RULE_VIOLATION'],
  MENU_ITEM_UNAVAILABLE: ['BUSINESS_RULE_VIOLATION'],
  MENU_AVAILABILITY_VIOLATION: ['BUSINESS_RULE_VIOLATION'],
  ACCOUNT_INACTIVE: ['AUTHORIZATION_DENIED'],
  STORE_ONBOARDING_INVALID_STATUS: ['BUSINESS_RULE_VIOLATION'],
  STORE_ONBOARDING_INCOMPLETE: ['BUSINESS_RULE_VIOLATION'],
  PHONE_NOT_SET: ['BUSINESS_RULE_VIOLATION'],
  PHONE_VERIFICATION_DISABLED: ['SERVICE_UNAVAILABLE'],
  PHONE_ALREADY_VERIFIED: ['CONFLICT'],
  PHONE_VERIFICATION_CODE_INVALID: ['BUSINESS_RULE_VIOLATION', 'INTERNAL_ERROR'],
  PHONE_VERIFICATION_CODE_EXPIRED: ['BUSINESS_RULE_VIOLATION'],
  PASSWORD_RESET_LINK_INVALID: ['BUSINESS_RULE_VIOLATION'],
  PHONE_VERIFICATION_RESEND_COOLDOWN: ['RATE_LIMITED'],
  PHONE_VERIFICATION_LANDLINE_UNSUPPORTED: ['BUSINESS_RULE_VIOLATION'],
  STEP_UP_REQUIRED: ['STEP_UP_REQUIRED'],
  PASSWORD_CONFIRMATION_REQUIRED: ['PASSWORD_CONFIRMATION_REQUIRED'],
  ADMIN_PANEL_REDIRECT_REQUIRED: ['ADMIN_PANEL_REDIRECT_REQUIRED'],
  TWO_FACTOR_NOT_ENABLED: ['AUTHORIZATION_DENIED'],
  STORE_TEMPORARILY_CLOSED: ['BUSINESS_RULE_VIOLATION'],
  ORDER_PAYMENT_METHOD_NOT_ATTACHED: ['BUSINESS_RULE_VIOLATION'],
  ORDER_PAYMENT_METHOD_NOT_USABLE: ['BUSINESS_RULE_VIOLATION'],
  ORDER_PAYMENT_METHOD_VALUE_INVALID: ['INTERNAL_ERROR'],
  PAYMENT_METHOD_DATA_INCOMPLETE: ['INTERNAL_ERROR'],
  INCOMPLETE_CARD_DETAILS: ['BUSINESS_RULE_VIOLATION'],
  UNSUPPORTED_PAYMENT_METHOD_TYPE: ['BUSINESS_RULE_VIOLATION'],
  ROW_LOCK_TIMEOUT: ['CONFLICT'],
  ORDER_MODIFICATION_PROPOSAL_STALE: ['CONFLICT'],
  ORDER_NOT_CANCELLABLE: ['BUSINESS_RULE_VIOLATION'],
  ORDER_AUTO_DECLINE_TOO_EARLY: ['BUSINESS_RULE_VIOLATION'],
  ORDER_NOT_AWAITING_PAYMENT: ['BUSINESS_RULE_VIOLATION'],
  ORDER_NOT_ELIGIBLE_FOR_PAYMENT_RETRY: ['BUSINESS_RULE_VIOLATION'],
  PAYMENT_ATTEMPT_IN_FLIGHT: ['BUSINESS_RULE_VIOLATION'],
  MERCHANT_ONBOARDING_CYCLE: ['BUSINESS_RULE_VIOLATION'],
  CONNECT_BUSINESS_TYPE_CHANGE_BLOCKED: ['BUSINESS_RULE_VIOLATION'],
  CONNECT_REONBOARDING_NOT_ACKNOWLEDGED: ['BUSINESS_RULE_VIOLATION'],
  BRAND_SETUP_REQUIRED: ['CONFLICT'],
  LEGAL_AGREEMENT_DOCUMENT_NOT_AVAILABLE: ['RESOURCE_NOT_FOUND'],
  LEGAL_AGREEMENT_NOT_ACCEPTED: ['BUSINESS_RULE_VIOLATION'],
  ASSET_STORAGE_UNAVAILABLE: ['SERVICE_UNAVAILABLE']
} satisfies Record<ApiProblemReason, readonly ApiErrorCode[]>

// Exhaustive over ApiProblemReason: locale key per leaf reason.
const apiProblemReasonKey = {
  PAYMENT_PROCESSING_FAILED: 'presentation.errors.paymentProcessingFailed',
  MENU_ITEM_UNAVAILABLE: 'presentation.errors.reasons.menuItemUnavailable',
  MENU_AVAILABILITY_VIOLATION: 'presentation.errors.reasons.menuAvailabilityViolation',
  ACCOUNT_INACTIVE: 'presentation.errors.reasons.accountInactive',
  STORE_ONBOARDING_INVALID_STATUS: 'presentation.errors.reasons.storeOnboardingInvalidStatus',
  STORE_ONBOARDING_INCOMPLETE: 'presentation.errors.reasons.storeOnboardingIncomplete',
  PHONE_NOT_SET: 'presentation.errors.reasons.phoneNotSet',
  PHONE_VERIFICATION_DISABLED: 'presentation.errors.reasons.phoneVerificationDisabled',
  PHONE_ALREADY_VERIFIED: 'presentation.errors.reasons.phoneAlreadyVerified',
  PHONE_VERIFICATION_CODE_INVALID: 'presentation.errors.reasons.phoneVerificationCodeInvalid',
  PHONE_VERIFICATION_CODE_EXPIRED: 'presentation.errors.reasons.phoneVerificationCodeExpired',
  PASSWORD_RESET_LINK_INVALID: 'presentation.errors.reasons.passwordResetLinkInvalid',
  PHONE_VERIFICATION_RESEND_COOLDOWN: 'presentation.errors.reasons.phoneVerificationResendCooldown',
  PHONE_VERIFICATION_LANDLINE_UNSUPPORTED:
    'presentation.errors.reasons.phoneVerificationLandlineUnsupported',
  STEP_UP_REQUIRED: 'presentation.errors.reasons.stepUpRequired',
  PASSWORD_CONFIRMATION_REQUIRED: 'presentation.errors.reasons.passwordConfirmationRequired',
  ADMIN_PANEL_REDIRECT_REQUIRED: 'presentation.errors.reasons.adminPanelRedirectRequired',
  TWO_FACTOR_NOT_ENABLED: 'presentation.errors.reasons.twoFactorNotEnabled',
  STORE_TEMPORARILY_CLOSED: 'presentation.errors.reasons.storeTemporarilyClosed',
  ORDER_PAYMENT_METHOD_NOT_ATTACHED: 'presentation.errors.reasons.orderPaymentMethodNotAttached',
  ORDER_PAYMENT_METHOD_NOT_USABLE: 'presentation.errors.reasons.orderPaymentMethodNotUsable',
  ORDER_PAYMENT_METHOD_VALUE_INVALID: 'presentation.errors.reasons.orderPaymentMethodValueInvalid',
  PAYMENT_METHOD_DATA_INCOMPLETE: 'presentation.errors.reasons.paymentMethodDataIncomplete',
  INCOMPLETE_CARD_DETAILS: 'presentation.errors.reasons.incompleteCardDetails',
  UNSUPPORTED_PAYMENT_METHOD_TYPE: 'presentation.errors.reasons.unsupportedPaymentMethodType',
  ROW_LOCK_TIMEOUT: 'presentation.errors.reasons.rowLockTimeout',
  ORDER_MODIFICATION_PROPOSAL_STALE: 'presentation.errors.reasons.orderModificationProposalStale',
  ORDER_NOT_CANCELLABLE: 'presentation.errors.reasons.orderNotCancellable',
  ORDER_AUTO_DECLINE_TOO_EARLY: 'presentation.errors.reasons.orderAutoDeclineTooEarly',
  ORDER_NOT_AWAITING_PAYMENT: 'presentation.errors.reasons.orderNotAwaitingPayment',
  ORDER_NOT_ELIGIBLE_FOR_PAYMENT_RETRY:
    'presentation.errors.reasons.orderNotEligibleForPaymentRetry',
  PAYMENT_ATTEMPT_IN_FLIGHT: 'presentation.errors.reasons.paymentAttemptInFlight',
  MERCHANT_ONBOARDING_CYCLE: 'presentation.errors.reasons.merchantOnboardingCycle',
  CONNECT_BUSINESS_TYPE_CHANGE_BLOCKED:
    'presentation.errors.reasons.connectBusinessTypeChangeBlocked',
  CONNECT_REONBOARDING_NOT_ACKNOWLEDGED:
    'presentation.errors.reasons.connectReonboardingNotAcknowledged',
  BRAND_SETUP_REQUIRED: 'presentation.errors.reasons.brandSetupRequired',
  LEGAL_AGREEMENT_DOCUMENT_NOT_AVAILABLE:
    'presentation.errors.reasons.legalAgreementDocumentNotAvailable',
  LEGAL_AGREEMENT_NOT_ACCEPTED: 'presentation.errors.reasons.legalAgreementNotAccepted',
  ASSET_STORAGE_UNAVAILABLE: 'presentation.errors.reasons.assetStorageUnavailable'
} satisfies Record<ApiProblemReason, string>

function buildApiErrorByReason(): Partial<Record<ApiErrorCode, Record<string, string>>> {
  const byReason: Partial<Record<ApiErrorCode, Record<string, string>>> = {}

  for (const reason of ApiProblemReasonSchema.options) {
    for (const broadCode of apiProblemReasonBroadCode[reason]) {
      const bucket = (byReason[broadCode] ??= {})
      bucket[reason] = apiProblemReasonKey[reason]
    }
  }

  return byReason
}

export const apiErrorResolverMap: ResolverMap<ApiErrorCode> = {
  byCode: apiErrorByCode,
  byReason: buildApiErrorByReason(),
  genericKey: 'presentation.errors.genericUnexpected'
}
